import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { adminAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { 
  Shield, Users, Building2, Briefcase, LogOut, Plus, 
  UserPlus, Building, BarChart3, Loader2, X, Edit, Trash2, Power,
  Eye, Mail, Phone, MapPin, FileText, Clock, CheckCircle, XCircle, Calendar,
  Upload, Download, FileSpreadsheet, AlertCircle, Image, Camera
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import NotificationPanel from '../components/NotificationPanel';

const STAGE_CONFIG = {
  applied: { label: 'Applied', color: 'bg-gray-500' },
  screening: { label: 'Screening', color: 'bg-yellow-500' },
  scheduled: { label: 'Scheduled', color: 'bg-blue-500' },
  interview: { label: 'Interview', color: 'bg-purple-500' },
  feedback: { label: 'Feedback', color: 'bg-indigo-500' },
  offer: { label: 'Offer', color: 'bg-green-500' },
  hired: { label: 'Hired', color: 'bg-emerald-600' },
  rejected: { label: 'Rejected', color: 'bg-red-500' },
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [companies, setCompanies] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [jobSeekers, setJobSeekers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal states
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showRecruiterModal, setShowRecruiterModal] = useState(false);
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [showEditRecruiterModal, setShowEditRecruiterModal] = useState(false);
  const [showJobSeekerModal, setShowJobSeekerModal] = useState(false);
  const [selectedJobSeeker, setSelectedJobSeeker] = useState(null);
  const [loadingJobSeeker, setLoadingJobSeeker] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingRecruiter, setEditingRecruiter] = useState(null);
  
  // Form data
  const [companyForm, setCompanyForm] = useState({
    companyName: '',
    industry: '',
    companySize: '',
    website: '',
    description: '',
    location: '',
    address: '',
    linkedinPage: '',
    contactPersonName: '',
    contactPersonDepartment: '',
    contactPersonDesignation: '',
    contactEmail: '',
    contactMobile: '',
    contactAlternateNumber: '',
    contactTelephone: '',
  });
  
  const [recruiterForm, setRecruiterForm] = useState({
    email: '',
    password: '',
    fullName: '',
    companyId: '',
    phone: '',
    designation: '',
  });

  const [editRecruiterForm, setEditRecruiterForm] = useState({
    fullName: '',
    companyId: '',
    phone: '',
    designation: '',
  });

  // Bulk upload states
  const [bulkUploadType, setBulkUploadType] = useState(null); // 'job-seekers' or 'jobs'
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Logo upload states
  const [uploadingLogo, setUploadingLogo] = useState(null); // company ID being uploaded

  // Download logs states
  const [downloadLogs, setDownloadLogs] = useState([]);
  const [downloadLogsLoading, setDownloadLogsLoading] = useState(false);
  const [downloadTodayStats, setDownloadTodayStats] = useState([]);
  const [downloadDailyLimit, setDownloadDailyLimit] = useState(50);

  const fetchDownloadLogs = async () => {
    try {
      setDownloadLogsLoading(true);
      const response = await adminAPI.getDownloadLogs({ limit: 100 });
      if (response.data.success) {
        setDownloadLogs(response.data.logs);
        setDownloadTodayStats(response.data.todayStats);
        setDownloadDailyLimit(response.data.dailyLimit);
      }
    } catch (error) {
      console.error('Error fetching download logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load download logs',
        variant: 'destructive',
      });
    } finally {
      setDownloadLogsLoading(false);
    }
  };

  const handleViewJobSeeker = async (seeker) => {
    try {
      setLoadingJobSeeker(true);
      setShowJobSeekerModal(true);
      const response = await adminAPI.getJobSeekerDetails(seeker.id);
      setSelectedJobSeeker(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load job seeker details',
        variant: 'destructive',
      });
    } finally {
      setLoadingJobSeeker(false);
    }
  };

  const getStageConfig = (stage) => {
    return STAGE_CONFIG[stage] || STAGE_CONFIG.applied;
  };

  // Bulk upload handlers
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const fileName = file.name.toLowerCase();
      if (!validTypes.some(type => fileName.endsWith(type))) {
        toast({
          title: 'Invalid File',
          description: 'Please upload a CSV or Excel file (.csv, .xlsx, .xls)',
          variant: 'destructive',
        });
        return;
      }
      setSelectedFile(file);
      setBulkUploadResult(null);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile || !bulkUploadType) return;
    
    try {
      setUploadingBulk(true);
      setBulkUploadResult(null);
      
      let response;
      if (bulkUploadType === 'job-seekers') {
        response = await adminAPI.bulkUploadJobSeekers(selectedFile);
      } else {
        response = await adminAPI.bulkUploadJobs(selectedFile);
      }
      
      setBulkUploadResult(response.data);
      
      if (response.data.created > 0) {
        toast({
          title: 'Upload Successful',
          description: `Created ${response.data.created} ${bulkUploadType === 'job-seekers' ? 'job seekers' : 'jobs'}`,
        });
        
        // Refresh data
        fetchDashboardData();
      }
    } catch (error) {
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.detail || 'Failed to process file',
        variant: 'destructive',
      });
    } finally {
      setUploadingBulk(false);
    }
  };

  const handleDownloadTemplate = async (type) => {
    try {
      const response = await adminAPI.downloadTemplate(type);
      
      // Create and download CSV file
      const blob = new Blob([response.data.content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: 'Template Downloaded',
        description: `${type} template downloaded successfully`,
      });
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: 'Failed to download template',
        variant: 'destructive',
      });
    }
  };

  const resetBulkUpload = () => {
    setBulkUploadType(null);
    setSelectedFile(null);
    setBulkUploadResult(null);
  };

  // Handle company logo upload
  const handleLogoUpload = async (companyId, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid File',
        description: 'Please upload a valid image file (JPEG, PNG, GIF, WebP, or SVG)',
        variant: 'destructive',
      });
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Please upload an image smaller than 5MB',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setUploadingLogo(companyId);
      const response = await adminAPI.uploadCompanyLogo(companyId, file);
      
      // Update local state with new logo URL
      setCompanies(prev => prev.map(c => 
        c.id === companyId ? { ...c, logo: response.data.logoUrl } : c
      ));
      
      toast({
        title: 'Success',
        description: 'Company logo uploaded successfully',
      });
    } catch (error) {
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.detail || 'Failed to upload logo',
        variant: 'destructive',
      });
    } finally {
      setUploadingLogo(null);
    }
  };

  // Handle company logo delete
  const handleDeleteLogo = async (companyId) => {
    try {
      setUploadingLogo(companyId);
      await adminAPI.deleteCompanyLogo(companyId);
      
      // Update local state to remove logo
      setCompanies(prev => prev.map(c => 
        c.id === companyId ? { ...c, logo: '' } : c
      ));
      
      toast({
        title: 'Success',
        description: 'Company logo removed successfully',
      });
    } catch (error) {
      toast({
        title: 'Delete Failed',
        description: error.response?.data?.detail || 'Failed to delete logo',
        variant: 'destructive',
      });
    } finally {
      setUploadingLogo(null);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const role = localStorage.getItem('userRole');

    if (!token || role !== 'super_admin') {
      toast({
        title: 'Access Denied',
        description: 'You must be a Super Admin to access this page',
        variant: 'destructive',
      });

      localStorage.clear();
      navigate('/admin/login');
      return;
    }
fetchDashboardData();
  }, [navigate]);

  // Fetch download logs when tab changes to downloadlogs
  useEffect(() => {
    if (activeTab === 'downloadlogs') {
      fetchDownloadLogs();
    }
  }, [activeTab]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, companiesRes, recruitersRes, jobSeekersRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getCompanies(),
        adminAPI.getRecruiters(),
        adminAPI.getJobSeekers(),
      ]);
      
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      setRecruiters(recruitersRes.data);
      setJobSeekers(jobSeekersRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/admin/login');
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await adminAPI.createCompany(companyForm);
      toast({
        title: 'Success',
        description: 'Company created successfully',
      });
      setShowCompanyModal(false);
      setCompanyForm({
        companyName: '',
        industry: '',
        companySize: '',
        website: '',
        description: '',
        location: '',
        address: '',
        linkedinPage: '',
        contactPersonName: '',
        contactPersonDepartment: '',
        contactPersonDesignation: '',
        contactEmail: '',
        contactMobile: '',
        contactAlternateNumber: '',
        contactTelephone: '',
      });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create company',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateRecruiter = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await adminAPI.createRecruiter(recruiterForm);
      toast({
        title: 'Success',
        description: 'Recruiter created successfully',
      });
      setShowRecruiterModal(false);
      setRecruiterForm({
        email: '',
        password: '',
        fullName: '',
        companyId: '',
        phone: '',
        designation: '',
      });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create recruiter',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      companyName: company.companyName || '',
      industry: company.industry || '',
      companySize: company.companySize || '',
      website: company.website || '',
      description: company.description || '',
      location: company.location || '',
      address: company.address || '',
      linkedinPage: company.linkedinPage || '',
      contactPersonName: company.contactPersonName || '',
      contactPersonDepartment: company.contactPersonDepartment || '',
      contactPersonDesignation: company.contactPersonDesignation || '',
      contactEmail: company.contactEmail || '',
      contactMobile: company.contactMobile || '',
      contactAlternateNumber: company.contactAlternateNumber || '',
      contactTelephone: company.contactTelephone || '',
    });
    setShowEditCompanyModal(true);
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await adminAPI.updateCompany(editingCompany.id, companyForm);
      toast({
        title: 'Success',
        description: 'Company updated successfully',
      });
      setShowEditCompanyModal(false);
      setEditingCompany(null);
      resetCompanyForm();
      fetchDashboardData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update company',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    if (!window.confirm(`Are you sure you want to delete "${companyName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await adminAPI.deleteCompany(companyId);
      toast({
        title: 'Success',
        description: 'Company deleted successfully',
      });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete company',
        variant: 'destructive',
      });
    }
  };

  const resetCompanyForm = () => {
    setCompanyForm({
      companyName: '',
      industry: '',
      companySize: '',
      website: '',
      description: '',
      location: '',
      address: '',
      linkedinPage: '',
      contactPersonName: '',
      contactPersonDepartment: '',
      contactPersonDesignation: '',
      contactEmail: '',
      contactMobile: '',
      contactAlternateNumber: '',
      contactTelephone: '',
    });
  };

  // Recruiter Management Functions
  const handleEditRecruiter = (recruiter) => {
    setEditingRecruiter(recruiter);
    setEditRecruiterForm({
      fullName: recruiter.fullName || '',
      companyId: recruiter.companyId || '',
      phone: recruiter.phone || '',
      designation: recruiter.designation || '',
    });
    setShowEditRecruiterModal(true);
  };

  const handleUpdateRecruiter = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await adminAPI.updateRecruiter(editingRecruiter.id, editRecruiterForm);
      toast({
        title: 'Success',
        description: 'Recruiter updated successfully',
      });
      setShowEditRecruiterModal(false);
      setEditingRecruiter(null);
      fetchDashboardData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update recruiter',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleRecruiterStatus = async (recruiterId, currentStatus) => {
    try {
      await adminAPI.toggleRecruiterStatus(recruiterId);
      toast({
        title: 'Success',
        description: `Recruiter ${currentStatus ? 'disabled' : 'enabled'} successfully`,
      });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteRecruiter = async (recruiterId, recruiterName) => {
    if (!window.confirm(`Are you sure you want to delete "${recruiterName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await adminAPI.deleteRecruiter(recruiterId);
      toast({
        title: 'Success',
        description: 'Recruiter deleted successfully',
      });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete recruiter',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Super Admin Dashboard</h1>
              <p className="text-sm text-gray-400">{localStorage.getItem('userEmail')}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={handleLogout}
            className="text-gray-400 hover:text-white hover:bg-gray-100"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-600 to-blue-700 p-6 border-0">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-blue-100 text-sm">Total Jobs</p>
                <p className="text-3xl font-bold text-white" data-testid="stat-total-jobs">{stats.totalJobs || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-600 to-purple-700 p-6 border-0">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-purple-100 text-sm">Companies</p>
                <p className="text-3xl font-bold text-white" data-testid="stat-total-companies">{stats.totalCompanies || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="bg-gradient-to-br from-cyan-600 to-cyan-700 p-6 border-0">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <UserPlus className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-cyan-100 text-sm">Recruiters</p>
                <p className="text-3xl font-bold text-white" data-testid="stat-total-recruiters">{stats.totalRecruiters || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-600 to-green-700 p-6 border-0">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-green-100 text-sm">Job Seekers</p>
                <p className="text-3xl font-bold text-white" data-testid="stat-total-seekers">{stats.totalJobSeekers || 0}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* HR Activity Notifications */}
        <div className="mb-8">
          <NotificationPanel 
            fetchNotifications={adminAPI.getNotifications}
            markRead={adminAPI.markNotificationRead}
            markAllRead={adminAPI.markAllNotificationsRead}
            title="HR Activity Updates"
          />
        </div>

        {/* Quick Actions */}
        <div className="flex gap-4 mb-8">
          <Dialog open={showCompanyModal} onOpenChange={setShowCompanyModal}>
            <DialogTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-700" data-testid="create-company-btn">
                <Building className="w-4 h-4 mr-2" />
                Create Company
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Create New Company
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCompany} className="space-y-4">
                {/* Basic Company Info */}
                <div className="border-b border-slate-600 pb-4 mb-4">
                  <h3 className="text-sm font-medium text-purple-400 mb-3">Company Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">Company Name *</Label>
                      <Input
                        required
                        value={companyForm.companyName}
                        onChange={(e) => setCompanyForm({...companyForm, companyName: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        data-testid="company-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Industry *</Label>
                      <Input
                        required
                        value={companyForm.industry}
                        onChange={(e) => setCompanyForm({...companyForm, industry: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="e.g., Information Technology"
                        data-testid="company-industry-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label className="text-gray-300">Company Size</Label>
                      <Input
                        value={companyForm.companySize}
                        onChange={(e) => setCompanyForm({...companyForm, companySize: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="e.g., 100-500"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Location (City)</Label>
                      <Input
                        value={companyForm.location}
                        onChange={(e) => setCompanyForm({...companyForm, location: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="e.g., Bangalore"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-gray-300">Full Address</Label>
                    <Input
                      value={companyForm.address}
                      onChange={(e) => setCompanyForm({...companyForm, address: e.target.value})}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="e.g., 123 Tech Park, Whitefield, Bangalore - 560066"
                      data-testid="company-address-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label className="text-gray-300">Website</Label>
                      <Input
                        type="url"
                        value={companyForm.website}
                        onChange={(e) => setCompanyForm({...companyForm, website: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="https://company.com"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">LinkedIn Page</Label>
                      <Input
                        type="url"
                        value={companyForm.linkedinPage}
                        onChange={(e) => setCompanyForm({...companyForm, linkedinPage: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="https://linkedin.com/company/..."
                        data-testid="company-linkedin-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Person Details */}
                <div className="border-b border-slate-600 pb-4 mb-4">
                  <h3 className="text-sm font-medium text-purple-400 mb-3">Contact Person Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">Contact Person Name</Label>
                      <Input
                        value={companyForm.contactPersonName}
                        onChange={(e) => setCompanyForm({...companyForm, contactPersonName: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="e.g., Rahul Sharma"
                        data-testid="contact-person-name"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Department</Label>
                      <Input
                        value={companyForm.contactPersonDepartment}
                        onChange={(e) => setCompanyForm({...companyForm, contactPersonDepartment: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="e.g., Human Resources"
                        data-testid="contact-department"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label className="text-gray-300">Designation</Label>
                      <Input
                        value={companyForm.contactPersonDesignation}
                        onChange={(e) => setCompanyForm({...companyForm, contactPersonDesignation: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="e.g., HR Manager"
                        data-testid="contact-designation"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Email</Label>
                      <Input
                        type="email"
                        value={companyForm.contactEmail}
                        onChange={(e) => setCompanyForm({...companyForm, contactEmail: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="hr@company.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Numbers */}
                <div>
                  <h3 className="text-sm font-medium text-purple-400 mb-3">Contact Numbers</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">Mobile Number</Label>
                      <Input
                        value={companyForm.contactMobile}
                        onChange={(e) => setCompanyForm({...companyForm, contactMobile: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="+91 98765 43210"
                        data-testid="contact-mobile"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Alternate Number</Label>
                      <Input
                        value={companyForm.contactAlternateNumber}
                        onChange={(e) => setCompanyForm({...companyForm, contactAlternateNumber: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="+91 98765 43211"
                        data-testid="contact-alternate"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-gray-300">Telephone</Label>
                    <Input
                      value={companyForm.contactTelephone}
                      onChange={(e) => setCompanyForm({...companyForm, contactTelephone: e.target.value})}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="080-12345678"
                      data-testid="contact-telephone"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-purple-600 hover:bg-purple-700 mt-4"
                  data-testid="submit-company-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Company
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showRecruiterModal} onOpenChange={setShowRecruiterModal}>
            <DialogTrigger asChild>
              <Button className="bg-cyan-600 hover:bg-cyan-700" data-testid="create-recruiter-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Create Recruiter
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Create New Recruiter
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateRecruiter} className="space-y-4">
                <div>
                  <Label className="text-gray-300">Full Name *</Label>
                  <Input
                    required
                    value={recruiterForm.fullName}
                    onChange={(e) => setRecruiterForm({...recruiterForm, fullName: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="recruiter-name-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Email *</Label>
                  <Input
                    type="email"
                    required
                    value={recruiterForm.email}
                    onChange={(e) => setRecruiterForm({...recruiterForm, email: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="recruiter-email-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Password *</Label>
                  <Input
                    type="password"
                    required
                    value={recruiterForm.password}
                    onChange={(e) => setRecruiterForm({...recruiterForm, password: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="recruiter-password-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Company *</Label>
                  <select
                    required
                    value={recruiterForm.companyId}
                    onChange={(e) => setRecruiterForm({...recruiterForm, companyId: e.target.value})}
                    className="w-full h-10 px-3 bg-slate-700 border border-slate-600 rounded-md text-white"
                    data-testid="recruiter-company-select"
                  >
                    <option value="">Select a company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.companyName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-300">Designation</Label>
                  <Input
                    value={recruiterForm.designation}
                    onChange={(e) => setRecruiterForm({...recruiterForm, designation: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="e.g., HR Manager"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                  data-testid="submit-recruiter-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Recruiter
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-slate-700">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === 'overview' 
                ? 'text-purple-400 border-b-2 border-purple-400' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('companies')}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === 'companies' 
                ? 'text-purple-400 border-b-2 border-purple-400' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Companies ({companies.length})
          </button>
          <button
            onClick={() => setActiveTab('recruiters')}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === 'recruiters' 
                ? 'text-purple-400 border-b-2 border-purple-400' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <UserPlus className="w-4 h-4 inline mr-2" />
            Recruiters ({recruiters.length})
          </button>
          <button
            onClick={() => setActiveTab('jobseekers')}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === 'jobseekers' 
                ? 'text-purple-400 border-b-2 border-purple-400' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Job Seekers ({jobSeekers.length})
          </button>
          <button
            onClick={() => { setActiveTab('bulkupload'); resetBulkUpload(); }}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === 'bulkupload' 
                ? 'text-purple-400 border-b-2 border-purple-400' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <Upload className="w-4 h-4 inline mr-2" />
            Bulk Upload
          </button>
          <button
            onClick={() => setActiveTab('downloadlogs')}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === 'downloadlogs' 
                ? 'text-purple-400 border-b-2 border-purple-400' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            data-testid="download-logs-tab"
          >
            <Download className="w-4 h-4 inline mr-2" />
            Download Logs
          </button>
        </div>

        {/* Content based on tab */}
        {activeTab === 'companies' && (
          <div className="grid gap-4">
            {companies.length === 0 ? (
              <Card className="bg-slate-800 border-slate-700 p-8 text-center">
                <Building2 className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">No companies yet. Create your first company!</p>
              </Card>
            ) : (
              companies.map(company => (
                <Card key={company.id} className="bg-slate-800 border-slate-700 p-5">
                  <div className="flex items-start gap-4">
                    {/* Company Logo */}
                    <div className="relative group">
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-700 flex items-center justify-center flex-shrink-0">
                        {company.logo ? (
                          <img 
                            src={company.logo.startsWith('http') ? company.logo : `${process.env.REACT_APP_BACKEND_URL}${company.logo}`}
                            alt={`${company.companyName} logo`}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`w-full h-full flex items-center justify-center ${company.logo ? 'hidden' : ''}`}>
                          <Building2 className="w-8 h-8 text-gray-500" />
                        </div>
                      </div>
                      {/* Logo upload overlay */}
                      <label 
                        className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-lg flex flex-col items-center justify-center"
                        data-testid={`upload-logo-${company.id}`}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleLogoUpload(company.id, e.target.files[0])}
                          disabled={uploadingLogo === company.id}
                        />
                        {uploadingLogo === company.id ? (
                          <Loader2 className="w-6 h-6 text-white animate-spin" />
                        ) : (
                          <>
                            <Camera className="w-5 h-5 text-white mb-1" />
                            <span className="text-white text-xs">{company.logo ? 'Change' : 'Upload'}</span>
                          </>
                        )}
                      </label>
                      {/* Delete logo button */}
                      {company.logo && (
                        <button
                          onClick={() => handleDeleteLogo(company.id)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          title="Remove logo"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    
                    {/* Company Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-white">{company.companyName}</h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-400 hover:text-blue-400 h-7 w-7 p-0"
                          onClick={() => handleEditCompany(company)}
                          data-testid={`edit-company-${company.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-400 hover:text-red-400 h-7 w-7 p-0"
                          onClick={() => handleDeleteCompany(company.id, company.companyName)}
                          data-testid={`delete-company-${company.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-gray-400 text-sm">{company.industry} • {company.location}</p>
                      {company.address && (
                        <p className="text-gray-500 text-xs mt-1">{company.address}</p>
                      )}
                      <div className="flex gap-4 mt-2">
                        {company.website && (
                          <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-sm hover:text-blue-300">
                            Website
                          </a>
                        )}
                        {company.linkedinPage && (
                          <a href={company.linkedinPage} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-sm hover:text-blue-300">
                            LinkedIn
                          </a>
                        )}
                      </div>
                    </div>
                    
                    {/* Contact Info */}
                    <div className="text-right">
                      {company.contactPersonName && (
                        <p className="text-gray-300 text-sm font-medium">{company.contactPersonName}</p>
                      )}
                      {company.contactPersonDesignation && (
                        <p className="text-gray-400 text-xs">{company.contactPersonDesignation}{company.contactPersonDepartment ? `, ${company.contactPersonDepartment}` : ''}</p>
                      )}
                      {company.contactMobile && (
                        <p className="text-gray-500 text-xs mt-1">{company.contactMobile}</p>
                      )}
                      <p className="text-gray-600 text-xs mt-1">{company.companySize} employees</p>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {activeTab === 'recruiters' && (
          <div className="grid gap-4">
            {recruiters.length === 0 ? (
              <Card className="bg-slate-800 border-slate-700 p-8 text-center">
                <UserPlus className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">No recruiters yet. Create a company first, then add recruiters!</p>
              </Card>
            ) : (
              recruiters.map(recruiter => (
                <Card key={recruiter.id} className={`bg-slate-800 border-slate-700 p-5 ${!recruiter.isActive ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-white">{recruiter.fullName}</h3>
                        {!recruiter.isActive && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">Disabled</span>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-400 hover:text-blue-400 h-7 w-7 p-0"
                          onClick={() => handleEditRecruiter(recruiter)}
                          data-testid={`edit-recruiter-${recruiter.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-7 w-7 p-0 ${recruiter.isActive ? 'text-green-400 hover:text-red-400' : 'text-red-400 hover:text-green-400'}`}
                          onClick={() => handleToggleRecruiterStatus(recruiter.id, recruiter.isActive)}
                          title={recruiter.isActive ? 'Disable Recruiter' : 'Enable Recruiter'}
                          data-testid={`toggle-recruiter-${recruiter.id}`}
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-400 hover:text-red-400 h-7 w-7 p-0"
                          onClick={() => handleDeleteRecruiter(recruiter.id, recruiter.fullName)}
                          data-testid={`delete-recruiter-${recruiter.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-gray-400 text-sm">{recruiter.email}</p>
                      {recruiter.designation && (
                        <p className="text-gray-500 text-xs mt-1">{recruiter.designation}</p>
                      )}
                      {recruiter.companyName && (
                        <p className="text-cyan-400 text-sm mt-1">
                          <Building className="w-3 h-3 inline mr-1" />
                          {recruiter.companyName}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      {recruiter.phone && (
                        <p className="text-gray-400 text-sm">{recruiter.phone}</p>
                      )}
                      <p className="text-gray-500 text-xs mt-1">
                        Last login: {recruiter.lastLogin ? new Date(recruiter.lastLogin).toLocaleDateString() : 'Never'}
                      </p>
                      <p className="text-gray-600 text-xs">
                        Created: {recruiter.createdAt ? new Date(recruiter.createdAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {activeTab === 'jobseekers' && (
          <div className="grid gap-4">
            {jobSeekers.length === 0 ? (
              <Card className="bg-slate-800 border-slate-700 p-8 text-center">
                <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">No job seekers registered yet.</p>
              </Card>
            ) : (
              jobSeekers.map(seeker => (
                <Card key={seeker.id} className="bg-slate-800 border-slate-700 p-5 hover:border-purple-500/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg">
                        {seeker.fullName?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-white">{seeker.fullName}</h3>
                          {seeker.hasProfile && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">Profile Complete</span>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {seeker.email}
                        </p>
                        {seeker.mobile && (
                          <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                            <Phone className="w-3 h-3" />
                            {seeker.mobile}
                          </p>
                        )}
                        {seeker.headline && (
                          <p className="text-cyan-400 text-sm mt-1">{seeker.headline}</p>
                        )}
                        {seeker.location && (
                          <p className="text-gray-500 text-xs flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            {seeker.location}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs ${seeker.applicationCount > 0 ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-600/20 text-gray-500'}`}>
                          <Briefcase className="w-3 h-3 inline mr-1" />
                          {seeker.applicationCount} Application{seeker.applicationCount !== 1 ? 's' : ''}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-400 hover:text-purple-400 h-8 w-8 p-0"
                          onClick={() => handleViewJobSeeker(seeker)}
                          data-testid={`view-seeker-${seeker.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-gray-500 text-xs">
                        Joined: {seeker.createdAt ? new Date(seeker.createdAt).toLocaleDateString() : 'N/A'}
                      </p>
                      {seeker.lastLogin && (
                        <p className="text-gray-600 text-xs">
                          Last active: {new Date(seeker.lastLogin).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Bulk Upload Tab */}
        {activeTab === 'bulkupload' && (
          <div className="space-y-6">
            {!bulkUploadType ? (
              /* Upload Type Selection */
              <div className="grid md:grid-cols-2 gap-6">
                <Card 
                  className="bg-slate-800 border-slate-700 p-6 cursor-pointer hover:border-purple-500 transition-colors"
                  onClick={() => setBulkUploadType('job-seekers')}
                  data-testid="bulk-upload-jobseekers-card"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Bulk Upload Job Seekers</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Upload multiple job seekers at once via CSV or Excel file
                    </p>
                    <Button 
                      variant="outline" 
                      className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
                      onClick={(e) => { e.stopPropagation(); handleDownloadTemplate('job-seekers'); }}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                </Card>

                <Card 
                  className="bg-slate-800 border-slate-700 p-6 cursor-pointer hover:border-blue-500 transition-colors"
                  onClick={() => setBulkUploadType('jobs')}
                  data-testid="bulk-upload-jobs-card"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
                      <Briefcase className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Bulk Upload Job Postings</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Upload multiple job postings at once via CSV or Excel file
                    </p>
                    <Button 
                      variant="outline" 
                      className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                      onClick={(e) => { e.stopPropagation(); handleDownloadTemplate('jobs'); }}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                </Card>
              </div>
            ) : (
              /* File Upload Interface */
              <Card className="bg-slate-800 border-slate-700 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      bulkUploadType === 'job-seekers' 
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                        : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                    }`}>
                      {bulkUploadType === 'job-seekers' ? (
                        <Users className="w-6 h-6 text-white" />
                      ) : (
                        <Briefcase className="w-6 h-6 text-white" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        Bulk Upload {bulkUploadType === 'job-seekers' ? 'Job Seekers' : 'Job Postings'}
                      </h3>
                      <p className="text-gray-400 text-sm">Upload CSV or Excel file</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-400 hover:text-white"
                    onClick={resetBulkUpload}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                {/* Template Download */}
                <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="w-5 h-5 text-green-400" />
                      <div>
                        <p className="text-white font-medium">Download Template</p>
                        <p className="text-gray-400 text-xs">Get the CSV template with required columns</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-green-500 text-green-400 hover:bg-green-500/10"
                      onClick={() => handleDownloadTemplate(bulkUploadType)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Template
                    </Button>
                  </div>
                </div>

                {/* File Upload Area */}
                <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center mb-6 hover:border-purple-500/50 transition-colors">
                  <input
                    type="file"
                    id="bulk-file-upload"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <label htmlFor="bulk-file-upload" className="cursor-pointer">
                    <Upload className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                    {selectedFile ? (
                      <div>
                        <p className="text-white font-medium mb-1">{selectedFile.name}</p>
                        <p className="text-gray-400 text-sm">
                          {(selectedFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-white font-medium mb-1">Click to upload or drag and drop</p>
                        <p className="text-gray-400 text-sm">CSV or Excel files (.csv, .xlsx, .xls)</p>
                      </div>
                    )}
                  </label>
                </div>

                {/* Upload Button */}
                <Button
                  className={`w-full ${
                    bulkUploadType === 'job-seekers'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                      : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700'
                  }`}
                  onClick={handleBulkUpload}
                  disabled={!selectedFile || uploadingBulk}
                  data-testid="bulk-upload-submit"
                >
                  {uploadingBulk ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload {bulkUploadType === 'job-seekers' ? 'Job Seekers' : 'Jobs'}
                    </>
                  )}
                </Button>

                {/* Upload Results */}
                {bulkUploadResult && (
                  <div className="mt-6 space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-white">{bulkUploadResult.totalProcessed}</p>
                        <p className="text-gray-400 text-sm">Total Processed</p>
                      </div>
                      <div className="bg-green-500/20 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-green-400">{bulkUploadResult.created}</p>
                        <p className="text-gray-400 text-sm">Created</p>
                      </div>
                      <div className="bg-red-500/20 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-red-400">{bulkUploadResult.skipped}</p>
                        <p className="text-gray-400 text-sm">Skipped</p>
                      </div>
                    </div>

                    {bulkUploadResult.errors && bulkUploadResult.errors.length > 0 && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <AlertCircle className="w-5 h-5 text-red-400" />
                          <h4 className="text-red-400 font-medium">Errors ({bulkUploadResult.errors.length})</h4>
                        </div>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {bulkUploadResult.errors.map((error, idx) => (
                            <p key={idx} className="text-red-300 text-sm">{error}</p>
                          ))}
                        </div>
                      </div>
                    )}

                    {bulkUploadResult.created > 0 && (
                      <div className="flex items-center justify-center gap-2 text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        <span>Successfully created {bulkUploadResult.created} records!</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Required Fields Info */}
                <div className="mt-6 bg-slate-700/30 rounded-lg p-4">
                  <h4 className="text-gray-300 font-medium mb-2">Required Fields</h4>
                  {bulkUploadType === 'job-seekers' ? (
                    <div className="text-gray-400 text-sm space-y-1">
                      <p><span className="text-red-400">*</span> email, fullName, password</p>
                      <p className="text-gray-500">Optional: mobile, experience, location, skills, currentSalary, expectedSalary, noticePeriod, headline, industry, currentRole</p>
                    </div>
                  ) : (
                    <div className="text-gray-400 text-sm space-y-1">
                      <p><span className="text-red-400">*</span> title, company, location</p>
                      <p className="text-gray-500">Optional: description, requirements, skills, experience, jobType, workMode, salaryMin, salaryMax, department, industry</p>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Download Logs Tab */}
        {activeTab === 'downloadlogs' && (
          <div className="space-y-6">
            {downloadLogsLoading ? (
              <Card className="bg-slate-800 border-slate-700 p-8 text-center">
                <Loader2 className="w-8 h-8 text-purple-500 animate-spin mx-auto mb-4" />
                <p className="text-gray-400">Loading download logs...</p>
              </Card>
            ) : (
              <>
                {/* Today's Stats Summary */}
                <Card className="bg-slate-800 border-slate-700 p-6">
                  <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-400" />
                    Today's Download Summary
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">Daily limit per recruiter: {downloadDailyLimit} candidates</p>
                  
                  {downloadTodayStats.length === 0 ? (
                    <p className="text-gray-400 text-center py-4">No downloads recorded today</p>
                  ) : (
                    <div className="grid gap-3">
                      {downloadTodayStats.map((stat, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center justify-between bg-slate-700/50 rounded-lg p-4"
                          data-testid={`today-stat-${idx}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                              <Users className="w-5 h-5 text-purple-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{stat.recruiterName || stat._id}</p>
                              <p className="text-gray-400 text-sm">{stat._id}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-white">{stat.totalDownloaded}</p>
                            <p className="text-gray-400 text-sm">{stat.downloadCount} export{stat.downloadCount !== 1 ? 's' : ''}</p>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-sm ${
                            stat.totalDownloaded >= downloadDailyLimit 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-green-500/20 text-green-400'
                          }`}>
                            {downloadDailyLimit - stat.totalDownloaded} left
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                {/* Download History */}
                <Card className="bg-slate-800 border-slate-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                      <FileSpreadsheet className="w-5 h-5 text-blue-400" />
                      Download History
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
                      onClick={fetchDownloadLogs}
                    >
                      <Loader2 className={`w-4 h-4 mr-2 ${downloadLogsLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                  
                  {downloadLogs.length === 0 ? (
                    <div className="text-center py-8">
                      <Download className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                      <p className="text-gray-400">No download logs found</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-700">
                            <th className="text-left py-3 px-4 text-gray-400 font-medium">Recruiter</th>
                            <th className="text-left py-3 px-4 text-gray-400 font-medium">Date & Time</th>
                            <th className="text-left py-3 px-4 text-gray-400 font-medium">Format</th>
                            <th className="text-center py-3 px-4 text-gray-400 font-medium">Count</th>
                            <th className="text-left py-3 px-4 text-gray-400 font-medium">Candidates</th>
                          </tr>
                        </thead>
                        <tbody>
                          {downloadLogs.map((log, idx) => (
                            <tr 
                              key={log.id || idx} 
                              className="border-b border-slate-700/50 hover:bg-slate-700/30"
                              data-testid={`download-log-${idx}`}
                            >
                              <td className="py-3 px-4">
                                <p className="text-white font-medium">{log.recruiterName}</p>
                                <p className="text-gray-400 text-sm">{log.recruiterEmail}</p>
                              </td>
                              <td className="py-3 px-4">
                                <p className="text-white">
                                  {new Date(log.downloadedAt).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </p>
                                <p className="text-gray-400 text-sm">
                                  {new Date(log.downloadedAt).toLocaleTimeString('en-IN', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  log.downloadFormat === 'contact' 
                                    ? 'bg-blue-500/20 text-blue-400' 
                                    : 'bg-gray-500/20 text-gray-300'
                                }`}>
                                  {log.downloadFormat === 'contact' ? 'With Contact' : 'Basic'}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className="text-xl font-bold text-purple-400">{log.candidateCount}</span>
                              </td>
                              <td className="py-3 px-4">
                                <div className="max-w-xs">
                                  <p className="text-gray-300 text-sm truncate" title={log.candidateNames?.join(', ')}>
                                    {log.candidateNames?.slice(0, 3).join(', ')}
                                    {log.candidateNames?.length > 3 && ` +${log.candidateNames.length - 3} more`}
                                  </p>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </>
            )}
          </div>
        )}

        {activeTab === 'overview' && (
          <Card className="bg-slate-800 border-slate-700 p-8">
            <div className="text-center">
              <BarChart3 className="w-16 h-16 text-purple-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Welcome to Admin Dashboard</h2>
              <p className="text-gray-400 max-w-md mx-auto">
                Manage your platform by creating companies, adding recruiters, and monitoring job seekers.
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* Edit Company Modal */}
      <Dialog open={showEditCompanyModal} onOpenChange={(open) => {
        setShowEditCompanyModal(open);
        if (!open) {
          setEditingCompany(null);
          resetCompanyForm();
        }
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Company - {editingCompany?.companyName}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateCompany} className="space-y-4">
            {/* Basic Company Info */}
            <div className="border-b border-slate-600 pb-4 mb-4">
              <h3 className="text-sm font-medium text-purple-400 mb-3">Company Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Company Name *</Label>
                  <Input
                    required
                    value={companyForm.companyName}
                    onChange={(e) => setCompanyForm({...companyForm, companyName: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="edit-company-name"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Industry *</Label>
                  <Input
                    required
                    value={companyForm.industry}
                    onChange={(e) => setCompanyForm({...companyForm, industry: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="edit-company-industry"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <Label className="text-gray-300">Company Size</Label>
                  <Input
                    value={companyForm.companySize}
                    onChange={(e) => setCompanyForm({...companyForm, companySize: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Location (City)</Label>
                  <Input
                    value={companyForm.location}
                    onChange={(e) => setCompanyForm({...companyForm, location: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-gray-300">Full Address</Label>
                <Input
                  value={companyForm.address}
                  onChange={(e) => setCompanyForm({...companyForm, address: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="edit-company-address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <Label className="text-gray-300">Website</Label>
                  <Input
                    type="url"
                    value={companyForm.website}
                    onChange={(e) => setCompanyForm({...companyForm, website: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">LinkedIn Page</Label>
                  <Input
                    type="url"
                    value={companyForm.linkedinPage}
                    onChange={(e) => setCompanyForm({...companyForm, linkedinPage: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="edit-company-linkedin"
                  />
                </div>
              </div>
            </div>

            {/* Contact Person Details */}
            <div className="border-b border-slate-600 pb-4 mb-4">
              <h3 className="text-sm font-medium text-purple-400 mb-3">Contact Person Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Contact Person Name</Label>
                  <Input
                    value={companyForm.contactPersonName}
                    onChange={(e) => setCompanyForm({...companyForm, contactPersonName: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Department</Label>
                  <Input
                    value={companyForm.contactPersonDepartment}
                    onChange={(e) => setCompanyForm({...companyForm, contactPersonDepartment: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <Label className="text-gray-300">Designation</Label>
                  <Input
                    value={companyForm.contactPersonDesignation}
                    onChange={(e) => setCompanyForm({...companyForm, contactPersonDesignation: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Email</Label>
                  <Input
                    type="email"
                    value={companyForm.contactEmail}
                    onChange={(e) => setCompanyForm({...companyForm, contactEmail: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Contact Numbers */}
            <div>
              <h3 className="text-sm font-medium text-purple-400 mb-3">Contact Numbers</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Mobile Number</Label>
                  <Input
                    value={companyForm.contactMobile}
                    onChange={(e) => setCompanyForm({...companyForm, contactMobile: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Alternate Number</Label>
                  <Input
                    value={companyForm.contactAlternateNumber}
                    onChange={(e) => setCompanyForm({...companyForm, contactAlternateNumber: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-gray-300">Telephone</Label>
                <Input
                  value={companyForm.contactTelephone}
                  onChange={(e) => setCompanyForm({...companyForm, contactTelephone: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setShowEditCompanyModal(false)}
                className="flex-1 border-slate-600 text-gray-300 hover:bg-gray-100"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                data-testid="update-company-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Update Company
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Recruiter Modal */}
      <Dialog open={showEditRecruiterModal} onOpenChange={(open) => {
        setShowEditRecruiterModal(open);
        if (!open) {
          setEditingRecruiter(null);
        }
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Recruiter
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateRecruiter} className="space-y-4">
            <div>
              <Label className="text-gray-300">Email (Read Only)</Label>
              <Input
                value={editingRecruiter?.email || ''}
                disabled
                className="bg-slate-700 border-slate-600 text-gray-400"
              />
            </div>
            <div>
              <Label className="text-gray-300">Full Name *</Label>
              <Input
                required
                value={editRecruiterForm.fullName}
                onChange={(e) => setEditRecruiterForm({...editRecruiterForm, fullName: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="edit-recruiter-name"
              />
            </div>
            <div>
              <Label className="text-gray-300">Company *</Label>
              <select
                required
                value={editRecruiterForm.companyId}
                onChange={(e) => setEditRecruiterForm({...editRecruiterForm, companyId: e.target.value})}
                className="w-full h-10 px-3 bg-slate-700 border border-slate-600 rounded-md text-white"
                data-testid="edit-recruiter-company"
              >
                <option value="">Select a company</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.companyName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-gray-300">Phone</Label>
              <Input
                value={editRecruiterForm.phone}
                onChange={(e) => setEditRecruiterForm({...editRecruiterForm, phone: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="+91 98765 43210"
                data-testid="edit-recruiter-phone"
              />
            </div>
            <div>
              <Label className="text-gray-300">Designation</Label>
              <Input
                value={editRecruiterForm.designation}
                onChange={(e) => setEditRecruiterForm({...editRecruiterForm, designation: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="e.g., HR Manager"
                data-testid="edit-recruiter-designation"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setShowEditRecruiterModal(false)}
                className="flex-1 border-slate-600 text-gray-300 hover:bg-gray-100"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                data-testid="update-recruiter-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Update Recruiter
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Job Seeker Details Modal */}
      <Dialog open={showJobSeekerModal} onOpenChange={(open) => {
        setShowJobSeekerModal(open);
        if (!open) {
          setSelectedJobSeeker(null);
        }
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5" />
              Job Seeker Details
            </DialogTitle>
          </DialogHeader>
          
          {loadingJobSeeker ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          ) : selectedJobSeeker ? (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-2xl">
                  {selectedJobSeeker.fullName?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-white">{selectedJobSeeker.fullName}</h2>
                  {selectedJobSeeker.profile?.headline && (
                    <p className="text-cyan-400">{selectedJobSeeker.profile.headline}</p>
                  )}
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {selectedJobSeeker.email}
                    </span>
                    {selectedJobSeeker.mobile && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {selectedJobSeeker.mobile}
                      </span>
                    )}
                    {selectedJobSeeker.profile?.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {selectedJobSeeker.profile.location}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Profile Details */}
              {selectedJobSeeker.profile && (
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-purple-400 mb-3">Profile Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {selectedJobSeeker.profile.experience && (
                      <div>
                        <p className="text-gray-500">Experience</p>
                        <p className="text-white">{selectedJobSeeker.profile.experience}</p>
                      </div>
                    )}
                    {selectedJobSeeker.profile.currentSalary && (
                      <div>
                        <p className="text-gray-500">Current Salary</p>
                        <p className="text-white">{selectedJobSeeker.profile.currentSalary}</p>
                      </div>
                    )}
                    {selectedJobSeeker.profile.expectedSalary && (
                      <div>
                        <p className="text-gray-500">Expected Salary</p>
                        <p className="text-white">{selectedJobSeeker.profile.expectedSalary}</p>
                      </div>
                    )}
                    {selectedJobSeeker.profile.noticePeriod && (
                      <div>
                        <p className="text-gray-500">Notice Period</p>
                        <p className="text-white">{selectedJobSeeker.profile.noticePeriod}</p>
                      </div>
                    )}
                  </div>
                  {selectedJobSeeker.profile.skills && selectedJobSeeker.profile.skills.length > 0 && (
                    <div className="mt-3">
                      <p className="text-gray-500 text-sm mb-2">Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedJobSeeker.profile.skills.map((skill, idx) => (
                          <span key={idx} className="px-2 py-1 bg-slate-600 rounded text-xs text-white">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedJobSeeker.profile.resumeUrl && (
                    <div className="mt-3">
                      <a 
                        href={selectedJobSeeker.profile.resumeUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
                      >
                        <FileText className="w-4 h-4" />
                        View Resume
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Applications Section */}
              <div>
                <h3 className="text-sm font-medium text-purple-400 mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Applications ({selectedJobSeeker.totalApplications || 0})
                </h3>
                {selectedJobSeeker.applications && selectedJobSeeker.applications.length > 0 ? (
                  <div className="space-y-3">
                    {selectedJobSeeker.applications.map((app) => {
                      const stageConfig = getStageConfig(app.currentStage);
                      return (
                        <div 
                          key={app.id} 
                          className="bg-slate-700/50 rounded-lg p-4 flex items-center justify-between"
                        >
                          <div>
                            <h4 className="font-medium text-white">{app.jobTitle}</h4>
                            <p className="text-gray-400 text-sm">{app.company}</p>
                            <p className="text-gray-500 text-xs mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Applied: {app.appliedAt ? new Date(app.appliedAt).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className={`px-3 py-1 rounded-full text-xs text-white ${stageConfig.color}`}>
                              {stageConfig.label}
                            </span>
                            {app.updatedAt && app.updatedAt !== app.appliedAt && (
                              <p className="text-gray-600 text-xs mt-2">
                                Updated: {new Date(app.updatedAt).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-slate-700/30 rounded-lg p-6 text-center">
                    <FileText className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500">No applications yet</p>
                  </div>
                )}
              </div>

              {/* Account Info */}
              <div className="border-t border-slate-600 pt-4 text-xs text-gray-500 flex gap-6">
                <span>Joined: {selectedJobSeeker.createdAt ? new Date(selectedJobSeeker.createdAt).toLocaleDateString() : 'N/A'}</span>
                <span>Last Login: {selectedJobSeeker.lastLogin ? new Date(selectedJobSeeker.lastLogin).toLocaleDateString() : 'Never'}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No data available
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
